import { useEffect, useState, useRef } from "react";
import { Cpu, Heart } from "lucide-react";
import { thesis } from "../data/mock";

const iconMap = {
  Cpu: Cpu,
  Heart: Heart,
};

const ThesisCard = ({ item, index }) => {
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const Icon = iconMap[item.icon];
  const isCyan = item.color === "cyan";

  return (
    <div
      ref={cardRef}
      className={`group relative p-8 rounded-2xl backdrop-blur-xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all duration-500 hover:scale-[1.02] transform ${
        visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Glow effect on hover */}
      <div
        className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
          isCyan
            ? "bg-gradient-to-br from-[#00F0FF]/5 to-transparent"
            : "bg-gradient-to-br from-orange-500/5 to-transparent"
        }`}
      />

      {/* Number */}
      <span className="font-mono text-sm text-[#6B7280] tracking-widest">
        {item.number}.
      </span>

      {/* Icon */}
      <div
        className={`w-14 h-14 rounded-xl flex items-center justify-center mt-4 mb-6 ${
          isCyan ? "bg-[#00F0FF]/10" : "bg-orange-500/10"
        }`}
      >
        <Icon
          className={`w-7 h-7 ${isCyan ? "text-[#00F0FF]" : "text-orange-400"}`}
        />
      </div>

      {/* Title */}
      <h3 className="font-mono text-xl text-[#F3F4F6] tracking-wide mb-4">
        {item.title}
      </h3>

      {/* Description */}
      <p className="font-sans text-[#6B7280] leading-relaxed">
        {item.description}
      </p>
    </div>
  );
};

const ThesisSection = () => {
  const [titleVisible, setTitleVisible] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTitleVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="thesis" className="py-32 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <div
          ref={titleRef}
          className={`text-center mb-16 transform transition-all duration-700 ${
            titleVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <h2 className="font-mono text-3xl md:text-4xl text-[#F3F4F6] tracking-wide">
            The WyCo Thesis
          </h2>
          <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-[#00F0FF]/50 to-transparent mx-auto mt-6" />
        </div>

        {/* Thesis Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {thesis.map((item, index) => (
            <ThesisCard key={item.id} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ThesisSection;
